import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate, useParams } from 'react-router';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormFieldValue } from '../forms/FormFieldValue';
import PageContainer from '../layout/PageContainer';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import InventoryItemForm, { InventoryItemFormState } from './InventoryItemForm';
import { GET_INVENTORYITEM_BY_ID_QUERY, GET_INVENTORYITEMS_QUERY } from '../../queries/InventoryItemQueries';
import { type InventoryItem, validate as validateInventoryItem } from '../../models/InventoryItem';
import { UPDATE_INVENTORYITEM_MUTATION } from '../../mutations/InventoryItemMutations';
import { GET_ITEMS_QUERY } from '../../queries/ItemQueries';

function InventoryItemEditForm({
  initialValues,
  onSubmit,
}: {
  initialValues: Partial<InventoryItemFormState['values']>;
  onSubmit: (id: number, formValues: Partial<InventoryItemFormState['values']>) => Promise<boolean>;
}) {
  const { inventoryItemId } = useParams();
  const navigate = useNavigate();

  const notifications = useNotifications();

  const { data: itemsData } = useQuery(
    GET_ITEMS_QUERY);

  const [formState, setFormState] = useState<InventoryItemFormState>(() => ({
    values: initialValues,
    errors: {},
  }));

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = useCallback(
    (newFormValues: Partial<InventoryItemFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = useCallback(
    (newFormErrors: Partial<InventoryItemFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = useCallback(
    (name: keyof InventoryItemFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<InventoryItemFormState['values']>) => {
        const { issues } = validateInventoryItem(values);
        setFormErrors({
          ...formErrors,
          [name]: issues?.find((issue) => issue.path?.[0] === name)?.message,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues],
  );

  const handleFormReset = useCallback(() => {
    setFormValues(initialValues);
  }, [initialValues, setFormValues]);

  const handleFormSubmit = useCallback(async () => {
    const { issues } = validateInventoryItem(formValues);

    if (issues && issues.length > 0) {
      setFormErrors(
        Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message])),
      );
      return;
    }

    setFormErrors({});

    const id = +(inventoryItemId || '0');

    if (id > 0) {
      try {
        const success = await onSubmit(id, formValues);

        if (success) {
          notifications.show('Inventory item edited successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });

          navigate(`/dashboard/inventory-items/${id}`);
        } else {
          notifications.show(
            'Failed to edit inventory item. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (editError) {
        notifications.show(
          `Failed to edit inventory item. Reason: ${(editError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
        throw editError;
      }
    }
  }, [formValues, inventoryItemId, navigate, notifications, onSubmit, setFormErrors]);

  return (
    <InventoryItemForm
      isFromNewPurchaseOrder={false}
      formState={formState}
      items={itemsData?.items || []}
      onFieldChange={handleFormFieldChange}
      onSubmit={handleFormSubmit}
      onReset={handleFormReset}
      submitButtonLabel="Save"
      backButtonPath={`/dashboard/inventory-items/${inventoryItemId}`}
    />
  );
}

export default function InventoryItemEdit() {
  const { inventoryItemId } = useParams();

  const [getInventoryItemById] = useLazyQuery(
    GET_INVENTORYITEM_BY_ID_QUERY);

  const [updateInventoryItem] = useMutation(
    UPDATE_INVENTORYITEM_MUTATION, {
    refetchQueries: [
      { query: GET_INVENTORYITEMS_QUERY },
      { query: GET_INVENTORYITEM_BY_ID_QUERY, variables: { id: +(inventoryItemId || '0') } },
      { query: GET_ITEMS_QUERY }],
  });

  const [inventoryItem, setInventoryItem] = useState<InventoryItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(inventoryItemId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getInventoryItemByIdError } = await getInventoryItemById({ variables: { id } });

        setIsLoading(false);
        setError(null);

        if (getInventoryItemByIdError) {
          setError(getInventoryItemByIdError);
        }

        if (data?.inventoryItem) {
          setInventoryItem(data.inventoryItem);
        } else {
          setError({ message: 'No inventory item found' } as Error);
        }

      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getInventoryItemById, inventoryItemId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = useCallback(
    async (id: number, formValues: Partial<InventoryItemFormState['values']>) => {
      setIsLoading(true);

      const { data: result } = await updateInventoryItem({
        variables: {
          id: id,
          input: {
            id: id,
            quantityOnHand: formValues.quantityOnHand,
            lotNumber: formValues.lotNumber,
            orderDetails: formValues.orderDetails,
            costPerUnit: formValues.costPerUnit || 0,
            surchargePerUnit: formValues.surchargePerUnit,
            shippingCostPerUnit: formValues.shippingCostPerUnit,
            surchargeOrDiscount: formValues.surchargeOrDiscount
          }
        }
      });

      setIsLoading(false);

      if (result?.updatedInventoryItem) {
        setInventoryItem(result.updatedInventoryItem);
        return true;
      }

      return false;
    },
    [updateInventoryItem],
  );

  const renderEdit = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return inventoryItem ? (
      <InventoryItemEditForm initialValues={inventoryItem} onSubmit={handleSubmit} />
    ) : null;
  }, [isLoading, error, inventoryItem, handleSubmit]);

  return (
    <PageContainer
      title={`Edit ${inventoryItem?.item?.name} (${inventoryItem?.vendor?.name}: ${inventoryItem?.lotNumber || 'N/A'})`}
      breadcrumbs={[
        { title: 'Items', path: '/dashboard/items' },
        { title: `${inventoryItem?.item?.name}`, path: `/dashboard/inventory-items/${inventoryItemId}` },
        { title: 'Edit' },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1 }}>{renderEdit}</Box>
    </PageContainer>
  );
}
